const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

export interface ActivityDay {
  key: string
  label: string
  events: number
  recordedIds: number
}

export interface ActivitySummary {
  days: ActivityDay[]
  featureCounts: Array<{ name: string; count: number }>
  validEvents: number
  eventsWithoutId: number
  invalidTimestamps: number
  start: Date
  end: Date
}

export function completedIstDays(now: Date, count = 30): { start: Date; end: Date; keys: string[] } {
  const shifted = new Date(now.getTime() + IST_OFFSET_MS)
  const todayStart = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate()) - IST_OFFSET_MS
  const end = new Date(todayStart)
  const start = new Date(todayStart - count * DAY_MS)
  const keys = Array.from({ length: count }, (_, index) =>
    new Date(todayStart - (count - index) * DAY_MS + IST_OFFSET_MS).toISOString().slice(0, 10))
  return { start, end, keys }
}

function timestampMillis(value: unknown): number | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime()
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return timestampMillis((value as { toDate: () => Date }).toDate())
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const time = new Date(value).getTime()
    return Number.isNaN(time) ? null : time
  }
  return null
}

function recordedId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const id = value.trim()
  return id && id.length <= 128 && !/^(null|undefined|anonymous)$/i.test(id) ? id : null
}

export function summarizeActivity(records: Array<Record<string, unknown>>, now: Date, count = 30): ActivitySummary {
  const window = completedIstDays(now, count)
  const eventCounts = new Map(window.keys.map(key => [key, 0]))
  const ids = new Map(window.keys.map(key => [key, new Set<string>()]))
  const featureCounts = new Map<string, number>()
  let validEvents = 0
  let eventsWithoutId = 0
  let invalidTimestamps = 0

  for (const record of records) {
    const time = timestampMillis(record.timestamp)
    if (time === null || time < window.start.getTime() || time >= window.end.getTime()) {
      invalidTimestamps++
      continue
    }
    const key = new Date(time + IST_OFFSET_MS).toISOString().slice(0, 10)
    if (!eventCounts.has(key)) continue
    validEvents++
    eventCounts.set(key, (eventCounts.get(key) ?? 0) + 1)
    const id = recordedId(record.userId)
    if (id) ids.get(key)?.add(id)
    else eventsWithoutId++

    const eventType = typeof record.eventType === 'string' && record.eventType.trim()
      ? record.eventType.trim().slice(0, 40) : 'untyped event'
    const action = eventType === 'user_engagement' && typeof record.action === 'string' && record.action.trim()
      ? ` / ${record.action.trim().slice(0, 40)}` : ''
    const feature = eventType + action
    featureCounts.set(feature, (featureCounts.get(feature) ?? 0) + 1)
  }

  return {
    days: window.keys.map(key => ({
      key,
      label: `${key.slice(8)} ${new Intl.DateTimeFormat('en-IN', { month: 'short', timeZone: 'Asia/Kolkata' }).format(new Date(`${key}T00:00:00+05:30`))}`,
      events: eventCounts.get(key) ?? 0,
      recordedIds: ids.get(key)?.size ?? 0,
    })),
    featureCounts: [...featureCounts].map(([name, value]) => ({ name, count: value })).sort((a, b) => b.count - a.count),
    validEvents,
    eventsWithoutId,
    invalidTimestamps,
    ...window,
  }
}
